import { ActionContext, Module } from 'vuex'
import { AuthUser, User } from '../models'
import { RootState, UserState } from '@/store/state_models'

import LogsService from '@/services/LogsService'
import { User as UserApi } from '../models/api'
import { UsersService } from '../services'
import cloneDeep from 'lodash-es/cloneDeep'
import defaults from '@/config'
import firebase from 'firebase/compat/app'
import localStorage from 'store'
import moment from 'moment'
import { RouterRegistry } from '../util/routerRegistry'
import { transformUserApiData } from '../util/helper'
import { ensureActiveFirebaseAuthState } from '../util/firebase-auth'
import { clearPendoSession } from '../util/pendo'

let unsubscribe: () => void
export const user: Module<UserState, RootState> = {
  namespaced: true,
  state: {
    passwordUpdatedInOnboarding: false,
    internalUser: false,
    scopes:[],
    scopesAssignedOnly: [],
  },
  getters: {
    get: (state: UserState) => {
			return cloneDeep(state.user);
    },
		isAdminUser: (state: UserState) => {
			return ['admin', 'agency'].includes(state?.user?.role)
		},
    getScopes: (state: UserState) => {
			return state.scopes || [];
    },
    getScopesAssignedOnly: (state: UserState) => {
      return state.scopesAssignedOnly || [];
    },
    isScopesLoaded: (state: UserState) => {
      return state.user && state.scopes !== undefined;
    }
	},
  mutations: {
    set: (state: UserState, payload: object) => {
      state.user = cloneDeep(payload)
    },
    updatePasswordUpdatedInOnboarding(state: UserState, payload: boolean) {
      console.log('updatePasswordUpdatedInOnboarding', payload)
      state.passwordUpdatedInOnboarding = payload
    },
    setInternalUser(state: UserState, payload: boolean) {
      state.internalUser = payload
    },
    setUserData(state: UserState, userData: { [key: string]: any}) {
      state.scopes = userData.scopes
      state.scopesAssignedOnly = userData.scopesAssignedOnly
    }
  },
  actions: {
    get: {
      handler: async (context: ActionContext<UserState, RootState>) => {
        const router = RouterRegistry.getRouter()
        if (context.state.user) {
          return context.state.user
        }
        try {
          const credentials = new AuthUser(
            await context.dispatch('auth/get', null, { root: true })
          )
          const user = await UserApi.getById(credentials.userId)
          const userData = transformUserApiData(user.data);
          const data = { ...userData, id: user.id }
          context.commit('set', data)
          context.commit('setUserData', userData);
          return data
        } catch (err) {
          console.warn('Store/User: GET:', err)
          const currentUser = firebase.auth().currentUser
          if (currentUser) {
            const token = await currentUser.getIdToken()
            console.error('Store/User: GET: Token', token)
            // if (token) {
            //   const values = atob(token)
            //   console.error(values)
            // }
          }
          // missing or insufficient permissions handler
          if (err.message === 'Missing or insufficient permissions') {
            let authState, authLocalStorage, firebaseToken, claims

            try {
              authState = await context.dispatch('auth/get', null, { root: true })
              authLocalStorage = JSON.parse(atob(localStorage.get('a')))
              // console.log('authState', authState, authLocalStorage)
              if (authState?.userId && authLocalStorage?.userId && authState?.userId !== authLocalStorage?.userId) {
                console.log('Reloading page as userID different in state and storage')
                window.location.reload()
                return
              }
            } catch (error) {
              console.error('Error while state and localstorage user check',error)
            }

            // Gather Firebase token and claims for logging
            try {
              firebaseToken = await firebase?.auth()?.currentUser?.getIdToken()
              // Get Firebase token claims safely
              if (firebaseToken) {
                try {
                  const decodedToken = await firebase?.auth()?.currentUser?.getIdTokenResult()
                  claims = decodedToken
                } catch (claimsErr) {
                  console.warn('Store/User: GET > Failed to decode Firebase token claims:', claimsErr)
                }
              }
            } catch(err) {
              console.error('Error while getting firebase token and claims', err)
            }

            try {
              // Send comprehensive error details to logging service
              await LogsService.dump('frontend-logout', {
                instance: 'Global Permissions error handler',
                origin: 'Store/User: GET',
                error: {
                  message: err?.message,
                  code: err?.code,
                  stack: err?.stack,
                },
                page: window?.location?.href,
                route: {
                  name: router.currentRoute.value.name,
                  fullPath: router.currentRoute.value.fullPath
                },
                firebaseClaims: claims?.claims,
                firebaseToken,
                firebaseTokenMeta: {
                  issuedAt: claims?.issuedAtTime,
                  expirationTime: claims?.expirationTime
                }
              })
            } catch(err) {
              console.error('Error while logging firebase token and claims', err)
            }

            console.log('Error while auth/get in store - firebase auth.')
            await context.dispatch('logout', null, { root: true })
            await router.push({ name: 'login', query: { logout: 'true' } })
          }
        }
      },
    },
    syncAll: {
      root: true,
      handler: async (context: ActionContext<UserState, RootState>) => {
        const router = RouterRegistry.getRouter()
        let credentials: AuthUser
        try {
          credentials = new AuthUser(
            await context.dispatch('auth/get', null, { root: true })
          )
        } catch {
          return
        }

        unsubscribe = User.getStreamById(credentials.userId).onSnapshot(
          async (snapshot: firebase.firestore.DocumentSnapshot) => {
            const data = snapshot.data()
            // to get default permissions enforced in getter

            let user;
            try {
              user = await UserApi.getById(credentials.userId)
            } catch (error) {
              console.error('Error while getting user data in syncAll', error)
              // The user is not found or deleted in the database, log the user out
              await context.dispatch('logout', null, { root: true })
              await router.push({ name: 'login', query: { logout: 'true' } })
              return
            }

            const userData = transformUserApiData(user?.data)
            const {permissions, scopes, scopesAssignedOnly} = new User(userData)
            data.permissions = permissions
            data.scopes = scopes
            data.scopesAssignedOnly = scopesAssignedOnly

            let logoutDueToInvalidity = false
            let logoutDueToPasswordChange = false
            if (data && (data.last_password_change || data.last_force_logout)) {
              const loginDateFromStorage = localStorage.get('loginDate')
              if (!loginDateFromStorage) {
                logoutDueToPasswordChange = true
              } else {
                const loginDate = moment(loginDateFromStorage)
                const lastPasswordChange = data.last_password_change ? moment(data.last_password_change.toMillis()) : null
                const lastForceLogout = data.last_force_logout ? moment(data.last_force_logout.toMillis()) : null
                if (
                  loginDate &&
                  lastPasswordChange &&
                  loginDate.isBefore(lastPasswordChange)
                ) {
                  if (!context.state.passwordUpdatedInOnboarding) {
                    console.log(
                      'Logging out due to password change',
                      loginDate,
                      lastPasswordChange
                    )
                    logoutDueToPasswordChange = true
                  }
                }
                if (
                  loginDate &&
                  lastForceLogout &&
                  loginDate.isBefore(lastForceLogout)
                ) {
                  logoutDueToInvalidity = true
                }
              }
            }

            const internalUser = localStorage && localStorage.get('app_referer') === defaults.supportPortalUrl
            if (internalUser) context.commit('setInternalUser', true);
            if (!internalUser && data && !data.last_login_time) {
              logoutDueToPasswordChange = true
            }

            const isProxyUser = await context.rootGetters['auth/isProxyUser']
            if (isProxyUser) {
              logoutDueToPasswordChange = false
              logoutDueToInvalidity = false
            }

            if (data?.restrictSubAccount && (data?.role === User.TYPE_AGENCY && !data?.isAgencyOwner)) {
              const currentLocationId = router?.currentRoute?.value?.params?.location_id || ""
              if (currentLocationId && !(Object.keys(data?.locations || {}).includes(currentLocationId))) {
                router.push({ name: 'dashboard', replace: true })
              }
            }

            if (
              !data ||
              data.deleted === true ||
              (!data.isAgencyOwner && !data.is_active) ||
              (context.state &&
                context.state.user &&
                context.state.user.role !== data.role) ||
              logoutDueToPasswordChange ||
              logoutDueToInvalidity
            ) {
              try {
                if(isProxyUser){
                  const recentlyVisitedUsers = localStorage.get('recentlyVisitedUsers') || {};
                  delete recentlyVisitedUsers[snapshot.id]
                  localStorage.set('recentlyVisitedUsers', recentlyVisitedUsers)
                  await context.dispatch('auth/switchToMasterUser', null, { root: true })
                  return;
                }
                await context.dispatch('logout', null, { root: true })
                await router.push({ name: 'login', query: { logout: 'true' } })
              } catch (err) {
                console.error(err)
              }
            }
            context.commit('set', { ...data, id: snapshot.id })
            try {
              if (data.scopes) {
                context.commit('setUserData', data);

              } else {
                await context.dispatch('setUserData')
              }
            } catch (error) {
              console.error('Error while setting user data', error)
            }
          },
          async error => {
            let firebaseToken, decodedFirebaseToken, typeOfError
            // Gather Firebase token and claims for logging
            try {
              firebaseToken = await firebase?.auth()?.currentUser?.getIdToken()
              // Get Firebase token claims safely
              if (firebaseToken) {
                try {
                  decodedFirebaseToken = await firebase?.auth()?.currentUser?.getIdTokenResult()
                  // check if current location is in the firebase token locations
                  const currentLocationId = router?.currentRoute?.value?.params?.location_id || ""
                  if (currentLocationId && !(Object.keys(decodedFirebaseToken?.locations || {}).includes(currentLocationId))) {
                    typeOfError = 'location_not_in_token'
                    console.warn('Store/User: SyncAll > Location not in token:', currentLocationId)
                  }
                } catch (claimsErr) {
                  typeOfError = 'token_decode_error'
                  console.warn('Store/User: GET > Failed to decode Firebase token claims:', claimsErr)
                }
              } else {
                typeOfError = 'no_firebase_token'
                console.warn('Store/User: SyncAll > No Firebase token')
              }
            } catch(err) {
              typeOfError = 'firebase_get_id_token_error'
              console.error('Error while getting firebase token and claims', err)
            }

            try {
              // Send comprehensive error details to logging service
              await LogsService.dump('frontend-logout', {
                instance: 'Store/User: SyncAll',
                origin: 'users/syncAll',
                error: {
                  message: error?.message,
                  code: error?.code,
                  stack: error?.stack
                },
                page: window?.location?.href,
                route: {
                  name: router.currentRoute.value.name,
                  fullPath: router.currentRoute.value.fullPath
                },
                firebaseClaims: decodedFirebaseToken?.claims,
                firebaseToken,
                decodedFirebaseToken: {
                  issuedAt: decodedFirebaseToken?.issuedAtTime,
                  expirationTime: decodedFirebaseToken?.expirationTime
                },
                cause: typeOfError
              })
            } catch(err) {
              console.error('Error while logging firebase token and claims in syncAll', err)
            }
          }
        )
      },
    },
    deleteUserState: {
      handler: (context: ActionContext<UserState, RootState>) => {
        context.commit('set', undefined)
      },
    },
    setUserData: {
      handler: async (context: ActionContext<UserState, RootState>) => {
        const userData = await UsersService.read(context.state.user.id);
        context.commit('setUserData', userData);
      },
    },
    logout: {
      root: true,
      handler: async (context: ActionContext<UserState, RootState>) => {
        context.dispatch('deleteUserState')
        if (unsubscribe) {
          unsubscribe()
        }
        clearPendoSession()
      },
    },
    update: {
			handler: (context: ActionContext<UserState, RootState>, payload: object): Promise<UserState | undefined> => {
				return new Promise(async (resolve, reject) => {
					try {
					const userData = transformUserApiData(payload);
          context.commit('set', userData)
					resolve(userData);
					} catch {
						reject();
					}
				});
			},
		},
  },
}

